import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../ui/Icon';
import { Toggle, PageSkeleton } from '../../ui/primitives';
import { useAdminToast } from '../AdminShell';
import {
  STOCK_KEYS, StockCatalogItem, learnStockItems, patchStockItem, plural, saveStockConfig, stockErrorText, useStockConfig, useStockItems,
} from '../../magazyn/api';
import { addedPositions, useInvalidateStockConfig } from './shared';

/*
 * Magazyn → Produkty: katalog pozycji i ich ustawienia. Każda zmiana zapisuje się sama
 * (przełączniki od razu, pola tekstowe/liczbowe po 600 ms przerwy, Enter albo wyjściu z pola).
 * Zapis optymistyczny: tabela zmienia się od razu, a przy błędzie wraca do poprzedniej wartości.
 */

type Patch = Partial<Pick<StockCatalogItem, 'displayName' | 'icon' | 'tracked' | 'carryOver' | 'step' | 'safetyStock' | 'sortOrder'>>;
type ItemsData = { items: StockCatalogItem[]; icons: string[] };
type Filter = 'tracked' | 'untracked' | 'all';

const DEBOUNCE_MS = 600;

/* ---------- pole z opóźnionym zapisem ---------- */
function DraftField({
  value, onCommit, kind, placeholder, min, max, width, title, ariaLabel, allowEmpty,
}: {
  value: string;
  onCommit: (raw: string) => void;
  kind: 'text' | 'number';
  placeholder?: string;
  min?: number;
  max?: number;
  width?: number | string;
  title?: string;
  ariaLabel: string;
  allowEmpty?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const timer = useRef<number | null>(null);
  const last = useRef(value);
  const latest = useRef(value);
  latest.current = value;
  // Esc → blur() odpala onBlur synchronicznie, jeszcze ze starym `draft` w domknięciu — bez tej
  // flagi „cofnij" zapisywałoby właśnie wpisaną wartość.
  const cancelled = useRef(false);

  // Wartość z serwera (np. po cofnięciu błędu) — tylko gdy użytkownik akurat nie pisze.
  useEffect(() => {
    last.current = value;
    if (!focused.current) setDraft(value);
  }, [value]);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  function commit(raw: string) {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (raw === last.current) return;
    if (kind === 'number' && raw.trim() === '' && !allowEmpty) return;
    last.current = raw;
    onCommit(raw);
  }

  return (
    <input
      className="input mga-cell-input"
      type={kind === 'number' ? 'number' : 'text'}
      inputMode={kind === 'number' ? 'numeric' : undefined}
      value={draft}
      min={min}
      max={max}
      placeholder={placeholder}
      title={title}
      aria-label={ariaLabel}
      style={{ width }}
      onFocus={(e) => { focused.current = true; e.currentTarget.select(); }}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => commit(v), DEBOUNCE_MS);
      }}
      onBlur={() => {
        focused.current = false;
        if (cancelled.current) cancelled.current = false;
        else commit(draft);
        // Po zapisie pokazujemy wartość po normalizacji (przycięta liczba, obcięte spacje) —
        // także gdy po normalizacji nic się nie zmieniło i serwer nie dostał zapytania.
        window.setTimeout(() => { if (!focused.current) { setDraft(latest.current); last.current = latest.current; } }, 0);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { commit(draft); (e.target as HTMLInputElement).blur(); }
        if (e.key === 'Escape') { if (timer.current) window.clearTimeout(timer.current); cancelled.current = true; setDraft(value); last.current = value; (e.target as HTMLInputElement).blur(); }
      }}
    />
  );
}

/* ---------- wybór ikony (popover) ---------- */
function IconPicker({ value, icons, onPick }: { value: string; icons: string[]; onPick: (icon: string) => void }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Popover poza przewijaną tabelą (position: fixed), żeby nie przycinał go overflow.
  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const w = 264;
    const h = 220;
    const below = r.bottom + 6 + h < window.innerHeight;
    setPos({ top: below ? r.bottom + 6 : r.top - 6 - h, left: Math.min(r.left, window.innerWidth - w - 12) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (pop.current?.contains(t) || btn.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const list = icons.indexOf(value) >= 0 || !value ? icons : [value].concat(icons);
  return (
    <>
      <button ref={btn} type="button" className="mga-iconbtn" aria-expanded={open} title="Zmień ikonę" onClick={() => setOpen((v) => !v)}>
        <Icon name={value || 'package'} size={18} />
        <Icon name="chevron-down" size={12} className="mga-iconbtn-caret" />
      </button>
      {open && (
        <div ref={pop} className="mga-iconpop" style={{ top: pos.top, left: pos.left }} role="listbox" aria-label="Ikona produktu">
          {list.map((ic) => (
            <button
              key={ic}
              type="button"
              role="option"
              aria-selected={ic === value}
              className="mga-iconopt"
              title={ic}
              onClick={() => { onPick(ic); setOpen(false); }}
            >
              <Icon name={ic} size={20} />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------- zakładka ---------- */
export default function ProductsTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const invalidateConfig = useInvalidateStockConfig();
  const { data, isLoading, isError, error } = useStockItems();
  const { data: config } = useStockConfig();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('tracked');
  const [learning, setLearning] = useState(false);

  const items = useMemo(() => data?.items ?? [], [data]);
  const icons = data?.icons ?? [];

  // Ile pozycji ma dany produkt — przełącznik „Warianty razem" ma sens dla >1 wariantu.
  const perProduct = useMemo(() => {
    const m = new Map<string, number>();
    items.forEach((it) => { if (it.productId) m.set(it.productId, (m.get(it.productId) || 0) + 1); });
    return m;
  }, [items]);

  const counts = useMemo(() => {
    const tracked = items.filter((i) => i.tracked).length;
    return { tracked, untracked: items.length - tracked, all: items.length };
  }, [items]);

  const rows = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return items
      .filter((it) => (filter === 'all' ? true : filter === 'tracked' ? it.tracked : !it.tracked))
      .filter((it) => {
        if (!nq) return true;
        return [it.title, it.variantTitle, it.displayName, it.sku, it.barcode].some((s) => (s || '').toLowerCase().includes(nq));
      })
      .slice()
      .sort((a, b) =>
        (a.sortOrder ?? 1e9) - (b.sortOrder ?? 1e9) ||
        (a.displayName || a.title).localeCompare(b.displayName || b.title, 'pl') ||
        (a.variantTitle || '').localeCompare(b.variantTitle || '', 'pl'),
      );
  }, [items, q, filter]);

  async function patch(item: StockCatalogItem, p: Patch) {
    const prev = qc.getQueryData<ItemsData>(STOCK_KEYS.items);
    qc.setQueryData<ItemsData>(STOCK_KEYS.items, (old) =>
      old ? { ...old, items: old.items.map((it) => (it.id === item.id ? { ...it, ...p } : it)) } : old,
    );
    try {
      const r = await patchStockItem(item.id, p);
      qc.setQueryData<ItemsData>(STOCK_KEYS.items, (old) =>
        old ? { ...old, items: old.items.map((it) => (it.id === item.id ? r.item : it)) } : old,
      );
      qc.invalidateQueries({ queryKey: STOCK_KEYS.overview });
    } catch (e) {
      // Cofamy tylko tę pozycję — inne zapisy mogły się w międzyczasie udać.
      const before = prev?.items.find((it) => it.id === item.id);
      if (before) {
        qc.setQueryData<ItemsData>(STOCK_KEYS.items, (old) =>
          old ? { ...old, items: old.items.map((it) => (it.id === item.id ? before : it)) } : old,
        );
      }
      push(stockErrorText(e, 'Nie udało się zapisać produktu'), 'err');
    }
  }

  function commitNumber(item: StockCatalogItem, field: 'step' | 'safetyStock', raw: string, min: number, max: number) {
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n)) return;
    const v = Math.min(max, Math.max(min, n));
    if (v !== item[field]) void patch(item, { [field]: v } as Patch);
  }

  function commitSort(item: StockCatalogItem, raw: string) {
    if (raw.trim() === '') { if (item.sortOrder !== null) void patch(item, { sortOrder: null }); return; }
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n)) return;
    const v = Math.min(9999, Math.max(0, n));
    if (v !== item.sortOrder) void patch(item, { sortOrder: v });
  }

  async function learn() {
    setLearning(true);
    try {
      const r = await learnStockItems();
      qc.invalidateQueries({ queryKey: STOCK_KEYS.items });
      qc.invalidateQueries({ queryKey: STOCK_KEYS.overview });
      if (r.added > 0) push(`Dodano ${addedPositions(r.added)}`, 'ok');
      else push(`Brak nowych produktów — sprawdzono ${r.orders} ${plural(r.orders, ['zamówienie', 'zamówienia', 'zamówień'])}`, 'info');
    } catch (e) {
      push(stockErrorText(e, 'Nie udało się wczytać produktów'), 'err');
    } finally {
      setLearning(false);
    }
  }

  async function toggleGrouping(item: StockCatalogItem) {
    if (!item.productId || !config) return;
    const current = config.productGrouping?.[item.productId] ?? config.groupBy;
    const next = current === 'product' ? 'variant' : 'product';
    const msg = next === 'product'
      ? `Liczyć wszystkie warianty „${item.title}" razem jako jedną pozycję?\n\nStan tych pozycji zacznie się od zera w nowym układzie — ilości trzeba będzie wpisać od nowa.`
      : `Liczyć każdy wariant „${item.title}" osobno?\n\nStan tych pozycji zacznie się od zera w nowym układzie — ilości trzeba będzie wpisać od nowa.`;
    if (!window.confirm(msg)) return;
    try {
      await saveStockConfig({ productGrouping: { ...(config.productGrouping || {}), [item.productId]: next } });
      invalidateConfig();
      push(next === 'product' ? `${item.title}: warianty liczone razem` : `${item.title}: każdy wariant osobno`, 'ok');
    } catch (e) {
      push(stockErrorText(e, 'Nie udało się zmienić liczenia'), 'err');
    }
  }

  if (isLoading) return <PageSkeleton table />;
  if (isError) {
    return (
      <div className="card"><div className="empty">
        <div className="ei"><Icon name="circle-alert" size={26} /></div>
        <h3>Nie udało się wczytać produktów</h3>
        <p>{stockErrorText(error)}</p>
      </div></div>
    );
  }

  const fdefs: [Filter, string][] = [['tracked', 'Śledzone'], ['untracked', 'Nieśledzone'], ['all', 'Wszystkie']];

  return (
    <div className="card mga-products">
      <div className="mga-toolbar">
        <div className="search mga-search">
          <Icon name="search" size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj nazwy, wariantu, SKU…" aria-label="Szukaj produktu" />
          {q && <button className="mga-clear" onClick={() => setQ('')} aria-label="Wyczyść"><Icon name="x" size={14} /></button>}
        </div>
        <div className="chips">
          {fdefs.map(([id, lbl]) => (
            <button key={id} className="chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>
              {lbl}<span className="c">{counts[id]}</span>
            </button>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm mga-toolbar-end" onClick={learn} disabled={learning} title="Dopisuje do katalogu produkty z otwartych zamówień, których jeszcze nie ma">
          <Icon name={learning ? 'loader-circle' : 'wand-sparkles'} size={14} className={learning ? 'mga-spin' : undefined} />
          Wczytaj produkty ze wszystkich zamówień
        </button>
      </div>

      <div className="tablewrap">
        <table className="tbl mga-ptbl">
          <thead>
            <tr>
              <th title="Czy pozycja jest liczona w magazynie">Śledź</th>
              <th>Ikona</th>
              <th style={{ minWidth: 280 }}>Produkt</th>
              <th title="Nie zeruje się o godzinie resetu, np. konfitury">Bez resetu</th>
              <th title="+6 jednym dotknięciem w dostawie">Krok</th>
              <th title="Tyle sztuk ponad zamówienia dobieramy na zapas">Zapas</th>
              <th title="Kolejność na liście; puste = alfabetycznie">Kolejność</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((it) => {
              const variants = it.productId ? perProduct.get(it.productId) || 0 : 0;
              const groupMode = it.productId ? config?.productGrouping?.[it.productId] : undefined;
              const canGroup = !!it.productId && (variants > 1 || groupMode === 'product');
              const grouped = (groupMode ?? config?.groupBy) === 'product';
              const defaultName = it.variantTitle ? `${it.title} (${it.variantTitle})` : it.title;
              return (
                <tr key={it.id} className={it.tracked ? '' : 'mga-off'}>
                  <td><Toggle on={it.tracked} onChange={(v) => patch(it, { tracked: v })} /></td>
                  <td><IconPicker value={it.icon || 'package'} icons={icons} onPick={(ic) => patch(it, { icon: ic })} /></td>
                  <td>
                    <div className="mga-pname">
                      <DraftField
                        kind="text"
                        value={it.displayName || ''}
                        placeholder={defaultName}
                        ariaLabel={`Nazwa własna: ${defaultName}`}
                        title="Nazwa własna — pusta = nazwa z Shopify"
                        width="100%"
                        allowEmpty
                        onCommit={(raw) => {
                          const v = raw.trim().slice(0, 120);
                          if ((v || null) !== (it.displayName || null)) void patch(it, { displayName: v || null });
                        }}
                      />
                      <div className="mga-pmeta">
                        {it.displayName && <span className="mga-orig" title="Nazwa z Shopify">{defaultName}</span>}
                        {it.sku && <span className="mono mga-sku">{it.sku}</span>}
                        {canGroup && (
                          <button
                            type="button"
                            className={`mga-groupbtn ${grouped ? 'on' : ''}`}
                            onClick={() => toggleGrouping(it)}
                            title={grouped ? 'Warianty tego produktu liczone razem — kliknij, by liczyć osobno' : 'Każdy wariant liczony osobno — kliknij, by liczyć razem'}
                          >
                            <Icon name="layers" size={12} />
                            {grouped ? 'Warianty razem' : `${variants} ${plural(variants, ['wariant', 'warianty', 'wariantów'])} osobno`}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td><Toggle on={it.carryOver} onChange={(v) => patch(it, { carryOver: v })} /></td>
                  <td>
                    <div className="mga-numcell">
                      <span className="mga-prefix">+</span>
                      <DraftField kind="number" value={String(it.step)} min={1} max={999} width={64} ariaLabel="Krok w dostawie"
                        title="Krok / opakowanie: +N jednym dotknięciem w dostawie"
                        onCommit={(raw) => commitNumber(it, 'step', raw, 1, 999)} />
                    </div>
                  </td>
                  <td>
                    <DraftField kind="number" value={String(it.safetyStock)} min={0} max={9999} width={72} ariaLabel="Zapas bezpieczeństwa"
                      title="Tyle sztuk ponad zamówienia dobieramy na zapas"
                      onCommit={(raw) => commitNumber(it, 'safetyStock', raw, 0, 9999)} />
                  </td>
                  <td>
                    <DraftField kind="number" value={it.sortOrder == null ? '' : String(it.sortOrder)} min={0} max={9999} width={72}
                      placeholder="A–Z" ariaLabel="Kolejność" allowEmpty
                      title="Mniejsza liczba = wyżej na liście. Puste = alfabetycznie"
                      onCommit={(raw) => commitSort(it, raw)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <div className="empty" style={{ padding: '40px 16px' }}>
          <div className="ei"><Icon name={items.length ? 'search-x' : 'wheat'} size={26} /></div>
          <h3>{items.length ? 'Brak wyników' : 'Katalog jest pusty'}</h3>
          <p>{items.length ? 'Zmień filtr lub frazę.' : 'Wczytaj produkty z otwartych zamówień — pojawią się tu same.'}</p>
        </div>
      )}
      <div className="mga-foot">
        <Icon name="info" size={14} />
        Zmiany zapisują się same. <b>Krok</b> to wielkość opakowania (np. +6 bułek jednym dotknięciem), <b>zapas</b> — ile sztuk dobieramy ponad zamówienia, <b>bez resetu</b> — pozycja nie zeruje się o godzinie resetu.
      </div>
    </div>
  );
}
